import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { PlugsConnected, ArrowsClockwise, PaperPlaneTilt, Check, Warning, X as XIcon } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [pushes, setPushes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState({ api_url: '', api_token: '' });
  const [testResult, setTestResult] = useState(null);

  const reload = async () => {
    try {
      const [intRes, pushRes] = await Promise.all([
        api.get('/integrations'),
        api.get('/integrations/pro-email/pushes'),
      ]);
      setIntegrations(intRes.data);
      setPushes(pushRes.data.items);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const testConnection = async () => {
    setTestResult(null);
    try {
      const { data } = await api.post('/integrations/pro-email/test');
      setTestResult(data);
      if (data.status === 'ok') toast.success(data.message);
      else toast.error(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleIntegration = async (provider, enabled) => {
    try {
      await api.put(`/integrations/${provider}`, { is_enabled: enabled });
      toast.success(`${provider} ${enabled ? 'enabled' : 'disabled'}`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveConfig = async () => {
    try {
      await api.put('/integrations/pro_email', {
        config: { api_url: configForm.api_url, api_token: configForm.api_token },
      });
      toast.success('Pro-Email config saved');
      setShowConfig(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openConfig = () => {
    const proEmail = integrations.find(i => i.provider === 'pro_email');
    const cfg = proEmail?.config || {};
    setConfigForm({ api_url: cfg.api_url || '', api_token: cfg.api_token || '' });
    setShowConfig(true);
  };

  const proEmail = integrations.find(i => i.provider === 'pro_email');
  const hasConfig = !!(proEmail?.config?.api_url && proEmail?.config?.api_token);
  const sourceLabel = proEmail?.last_success_at ? (proEmail?.config?.api_url ? 'UI' : 'ENV') : 'Not tested';

  return (
    <div data-testid="integrations-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Integrations</h1>
        <p className="text-sm text-gray-500 mt-1">Manage external service connections. Pro-Email is the primary outreach destination. Push approved leads directly into a selected Pro-Email group.</p>
      </div>

      {/* Google Places Integration */}
      <GooglePlacesCard integrations={integrations} reload={reload} />

      {/* Pro-Email Integration */}
      <div className="border border-gray-200 rounded-md bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <PaperPlaneTilt size={24} className="text-blue-600" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">Pro-Email</h3>
              <p className="text-xs text-gray-500">Push approved leads directly into a group you select. No list is created.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${
              proEmail?.is_enabled
                ? (hasConfig ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200')
                : 'bg-gray-100 text-gray-500 border-gray-200'
            }`}>
              {proEmail?.is_enabled ? (hasConfig ? 'Enabled' : 'Enabled (not configured)') : 'Disabled'}
            </span>
            <Switch checked={proEmail?.is_enabled || false} onCheckedChange={(v) => toggleIntegration('pro_email', v)} data-testid="toggle-pro-email" />
          </div>
        </div>

        <div className="flex gap-3 mb-3">
          <Button variant="outline" size="sm" onClick={testConnection} data-testid="test-pro-email-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Test Connection
          </Button>
          <Button variant="outline" size="sm" onClick={openConfig} data-testid="configure-pro-email-btn">
            Configure API
          </Button>
        </div>

        {/* Config summary */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>API URL: <span className="font-mono">{proEmail?.config?.api_url || 'Not configured (set PRO_EMAIL_API_URL in .env or Configure below)'}</span></p>
          <p>API Token: <span className="font-mono">{proEmail?.config?.api_token ? '********' + proEmail.config.api_token.slice(-4) : 'Not set'}</span></p>
          {proEmail?.last_test_at && <p>Last tested: {new Date(proEmail.last_test_at).toLocaleString()} ({sourceLabel})</p>}
          {proEmail?.last_error && <p className="text-red-600">Last error: {proEmail.last_error}</p>}
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`mt-3 p-3 rounded-md text-sm ${testResult.status === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {testResult.message}
          </div>
        )}
      </div>

      {/* Configure dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Configure Pro-Email API</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-mono uppercase text-gray-500 mb-1 block">API URL</label>
              <Input value={configForm.api_url} onChange={e => setConfigForm(p => ({...p, api_url: e.target.value}))} placeholder="https://api.pro-email.app" data-testid="pro-email-url-input" />
              <p className="text-xs text-gray-400 mt-1">Env var PRO_EMAIL_API_URL always overrides this value.</p>
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-gray-500 mb-1 block">API Token</label>
              <Input type="password" value={configForm.api_token} onChange={e => setConfigForm(p => ({...p, api_token: e.target.value}))} placeholder="Bearer token" data-testid="pro-email-token-input" />
            </div>
            <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">
              Field mapping: email, first_name, last_name, name, salutation, language, title, company, website, phone, city, state, country, industry, tags. Only companies with status "approved" and a valid contact email will be pushed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
              <Button onClick={saveConfig} className="bg-black text-white hover:bg-gray-800" data-testid="save-pro-email-config">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Source Connectors */}
      <div className="border border-gray-200 rounded-md bg-white p-6 mb-6">
        <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-4">Source Connectors</h3>
        <ConnectorList />
      </div>

      {/* Push History */}
      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500">Pro-Email Push History</h3>
        </div>
        <table className="w-full">
          <thead><tr className="border-b border-gray-200 bg-gray-50/50">
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Company</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Email</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Destination</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Status</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Ext. ID</th>
            <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Date</th>
          </tr></thead>
          <tbody>
            {pushes.length === 0 ? (
              <tr><td colSpan="6" className="py-8 text-center text-gray-400 text-sm">No pushes yet. Approve companies and push them from the Companies page or here.</td></tr>
            ) : pushes.map(p => (
              <tr key={p.id} className="border-b border-gray-100">
                <td className="py-3 px-4 text-sm text-gray-900">{p.company_name}</td>
                <td className="py-3 px-4 text-sm font-mono text-gray-600">{p.email}</td>
                <td className="py-3 px-4 text-sm text-gray-600">{p.group_name || p.list_name || '-'}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${
                    p.status === 'pushed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    p.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-gray-100 text-gray-600 border-gray-200'
                  }`}>{p.status}</span>
                  {p.error && <p className="text-xs text-red-600 mt-0.5 max-w-xs truncate" title={p.error}>{p.error}</p>}
                </td>
                <td className="py-3 px-4 text-xs font-mono text-gray-400">{p.external_contact_id || '-'}</td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{p.pushed_at ? new Date(p.pushed_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConnectorList() {
  const [connectors, setConnectors] = useState([]);
  useEffect(() => { api.get('/discovery/connectors').then(r => setConnectors(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-3">
      {connectors.map(c => (
        <div key={c.id} className="flex items-center justify-between py-2">
          <div>
            <span className="text-sm font-medium text-gray-900">{c.name}</span>
            <span className={`ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase ${c.type === 'internet' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>{c.type}</span>
            <p className="text-xs text-gray-500">{c.description}</p>
          </div>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${
            c.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'
          }`}>{c.status}</span>
        </div>
      ))}
    </div>
  );
}

function GooglePlacesCard({ integrations, reload }) {
  const gp = integrations.find(i => i.provider === 'google_places');
  const hasKey = !!gp?.config?.api_key;
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    if (!apiKey.trim()) { toast.error('Paste an API key first'); return; }
    setSaving(true);
    try {
      await api.put('/integrations/google_places', {
        config: { api_key: apiKey.trim() },
        is_enabled: true,
      });
      toast.success('Google Places key saved');
      setApiKey('');
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/integrations/google-places/test');
      setTestResult(data);
      if (data.status === 'ok') toast.success(data.message);
      else toast.error(data.message);
      reload();
    } catch (err) {
      const msg = formatApiError(err);
      setTestResult({ status: 'error', message: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  return (
    <div className="border border-gray-200 rounded-md bg-white p-6 mb-6" data-testid="google-places-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <PlugsConnected size={24} className="text-amber-600" />
          <div>
            <h3 className="text-sm font-medium text-gray-900">Google Places (New)</h3>
            <p className="text-xs text-gray-500">Source real companies from Google. Requires the <span className="font-mono">Places API (New)</span> enabled with a billing account attached.</p>
          </div>
        </div>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${
          hasKey ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'
        }`}>
          {hasKey ? 'Key configured' : 'No key'}
        </span>
      </div>

      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 mb-3">
        <Input
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder={hasKey ? '••••••••••••• (saved — paste a new key to rotate)' : 'Paste AIza... key'}
          data-testid="google-places-key-input"
        />
        <Button onClick={save} disabled={saving} className="bg-black text-white hover:bg-gray-800" data-testid="save-google-places-key">
          {saving ? 'Saving...' : 'Save key'}
        </Button>
        <Button variant="outline" onClick={test} disabled={testing || !hasKey} data-testid="test-google-places-btn">
          {testing ? 'Testing...' : 'Test live search'}
        </Button>
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        {gp?.last_test_at && <p>Last tested: {new Date(gp.last_test_at).toLocaleString()}</p>}
        {gp?.last_error && <p className="text-red-600">Last error: {gp.last_error}</p>}
        {!hasKey && (
          <p className="text-amber-600">
            Setup: Enable <span className="font-mono">Places API (New)</span> at <span className="font-mono">console.cloud.google.com/apis/library</span>, attach a billing account, then paste the key above. Google Maps Platform requires billing even for the free tier.
          </p>
        )}
      </div>

      {testResult && (
        <div className={`mt-3 p-3 rounded-md text-sm ${testResult.status === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          <div className="font-medium">{testResult.message}</div>
          {testResult.samples?.length > 0 && (
            <div className="text-xs mt-1 opacity-80">Sample: {testResult.samples.join(' · ')}</div>
          )}
        </div>
      )}
    </div>
  );
}
